import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Trash2,
  Clock,
  ChevronDown,
  ChevronRight,
  Loader2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { ConfirmDialog } from '@/shared/components/confirm-dialog'
import { useActivites, useCreateActivite, useDeleteActivite } from '../hooks/use-activites'
import { useTypesActivite } from '@/features/types-activite/hooks/use-types-activite'
import type { WeekDay } from '@/shared/types/common'
import type { ActivityResponseDto, CreateActivityDto } from '@/shared/types/activite'
import type { TypeActivite } from '@/shared/types/admin'

// ── Constants ───────────────────────────────────────────

const WORK_DAYS: WeekDay[] = ['LUNDI', 'MARDI', 'MERCREDI', 'JEUDI', 'VENDREDI', 'SAMEDI', 'DIMANCHE']

const DAY_LABELS: Record<WeekDay, string> = {
  LUNDI: 'Lundi',
  MARDI: 'Mardi',
  MERCREDI: 'Mercredi',
  JEUDI: 'Jeudi',
  VENDREDI: 'Vendredi',
  SAMEDI: 'Samedi',
  DIMANCHE: 'Dimanche',
}

const DAY_COLORS: Record<string, { bg: string; border: string; accent: string }> = {
  LUNDI: { bg: 'bg-blue-50', border: 'border-blue-200', accent: 'text-blue-700' },
  MARDI: { bg: 'bg-violet-50', border: 'border-violet-200', accent: 'text-violet-700' },
  MERCREDI: { bg: 'bg-amber-50', border: 'border-amber-200', accent: 'text-amber-700' },
  JEUDI: { bg: 'bg-emerald-50', border: 'border-emerald-200', accent: 'text-emerald-700' },
  VENDREDI: { bg: 'bg-rose-50', border: 'border-rose-200', accent: 'text-rose-700' },
  SAMEDI: { bg: 'bg-cyan-50', border: 'border-cyan-200', accent: 'text-cyan-700' },
  DIMANCHE: { bg: 'bg-orange-50', border: 'border-orange-200', accent: 'text-orange-700' },
}

const REVISION_BLOCK_MINUTES = 45
const REVISION_BREAK_MINUTES = 15

// ── Helpers ─────────────────────────────────────────────

function formatDuration(start: string, end: string) {
  const [sh, sm] = start.split(':').map(Number)
  const [eh, em] = end.split(':').map(Number)
  const mins = eh * 60 + em - (sh * 60 + sm)
  const h = Math.floor(mins / 60)
  const m = mins % 60
  if (h === 0) return `${m}min`
  if (m === 0) return `${h}h`
  return `${h}h${m.toString().padStart(2, '0')}`
}

function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number)
  return h * 60 + m
}

function minutesToTime(mins: number): string {
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`
}

/** Split a time range into 45min blocks with 15min breaks (for Révisions) */
function splitRevisionBlocks(
  startTime: string,
  endTime: string,
): Array<{ startTime: string; endTime: string; blockNum: number }> {
  const start = timeToMinutes(startTime)
  const end = timeToMinutes(endTime)
  const totalMinutes = end - start

  if (totalMinutes <= REVISION_BLOCK_MINUTES) {
    return [{ startTime, endTime, blockNum: 1 }]
  }

  const blocks: Array<{ startTime: string; endTime: string; blockNum: number }> = []
  let current = start
  let blockNum = 1

  while (current < end) {
    const blockEnd = Math.min(current + REVISION_BLOCK_MINUTES, end)
    blocks.push({
      startTime: minutesToTime(current),
      endTime: minutesToTime(blockEnd),
      blockNum,
    })
    current = blockEnd + REVISION_BREAK_MINUTES
    blockNum++
  }

  return blocks
}

// ── Time slots (00:00 → 23:45, every 15 min) ───────────

const TIME_SLOTS: string[] = []
for (let h = 0; h <= 23; h++) {
  for (let m = 0; m < 60; m += 15) {
    TIME_SLOTS.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`)
  }
}

// ── Form hook (state + submit logic) ────────────────────

function useActivityFormState(etudiantId: number, typesActivite: TypeActivite[]) {
  const activeTypes = typesActivite.filter((ta) => ta.actif)

  const [typeActiviteId, setTypeActiviteId] = useState<number>(0)
  const [title, setTitle] = useState('')
  const [days, setDays] = useState<WeekDay[]>([])
  const [startTime, setStartTime] = useState('12:00')
  const [endTime, setEndTime] = useState('13:00')

  const selectedType = activeTypes.find((ta) => ta.id === typeActiviteId)
  const isRevisions = selectedType?.code === 'REVISIONS'

  const canSubmit =
    typeActiviteId > 0 &&
    days.length > 0 &&
    (isRevisions || title.trim().length > 0) &&
    startTime < endTime

  const toggleDay = (d: WeekDay) => {
    setDays((prev) => {
      if (prev.includes(d)) {
        return prev.filter((x) => x !== d)
      }
      return [...prev, d]
    })
  }

  const buildPayload = (): CreateActivityDto[] | null => {
    if (!canSubmit) return null
    const result: CreateActivityDto[] = []
    for (const day of days) {
      if (isRevisions) {
        const blocks = splitRevisionBlocks(startTime, endTime)
        for (const block of blocks) {
          result.push({
            studentId: etudiantId,
            typeActiviteId,
            title: blocks.length > 1 ? `Révisions - Bloc ${block.blockNum}` : 'Révisions',
            day,
            startTime: block.startTime,
            endTime: block.endTime,
          })
        }
      } else {
        result.push({
          studentId: etudiantId,
          typeActiviteId,
          title: title.trim(),
          day,
          startTime,
          endTime,
        })
      }
    }
    return result
  }

  const reset = () => { setTitle(''); setDays([]) }

  return {
    activeTypes, typeActiviteId, setTypeActiviteId, title, setTitle,
    days, toggleDay, startTime, setStartTime, endTime, setEndTime,
    selectedType, isRevisions, canSubmit, buildPayload, reset,
  }
}

// ── Activity Form ───────────────────────────────────────

interface ActivityFormProps {
  etudiantId: number
  typesActivite: TypeActivite[]
  isPending: boolean
  onSubmit: (activities: CreateActivityDto[]) => void
}

function ActivityForm({ etudiantId, typesActivite, isPending, onSubmit }: ActivityFormProps) {
  const form = useActivityFormState(etudiantId, typesActivite)

  const handleSubmit = () => {
    const payload = form.buildPayload()
    if (payload) { onSubmit(payload); form.reset() }
  }

  if (form.activeTypes.length === 0) return null

  const couleur = form.selectedType?.couleur ?? '#6B7280'
  const previewDay = form.days[0] ?? 'LUNDI'
  const dayColors = DAY_COLORS[previewDay]
  const displayTitle = form.isRevisions ? 'Révisions' : (form.title || 'Mon activité')

  return (
    <div className="rounded-xl border border-neutral-200 bg-white shadow-sm">
      <div className="flex">
        {/* Gauche : formulaire */}
        <div className="flex-1 border-r border-neutral-100 px-5 py-4">
          <div className="space-y-3">
            {/* Ligne 1 : Type chips */}
            <div>
              <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wider text-neutral-400">Type</label>
              <div className="flex flex-wrap gap-1">
                {form.activeTypes.map((ta) => {
                  const isSelected = form.typeActiviteId === ta.id
                  const c = ta.couleur ?? '#6B7280'
                  return (
                    <button
                      key={ta.id}
                      type="button"
                      onClick={() => form.setTypeActiviteId(ta.id)}
                      className={cn(
                        'inline-flex items-center gap-1 rounded-md border px-2 py-1.5 text-[11px] font-semibold transition-all',
                        isSelected ? 'shadow-sm' : 'border-neutral-200 bg-white text-neutral-500 hover:border-neutral-300',
                      )}
                      style={isSelected ? {
                        backgroundColor: c + '15',
                        color: c,
                        borderColor: c + '40',
                      } : {}}
                    >
                      <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: c }} />
                      {ta.libelle}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Ligne 2 : Titre */}
            {!form.isRevisions && (
              <div>
                <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wider text-neutral-400">Titre</label>
                <input
                  value={form.title}
                  onChange={(e) => form.setTitle(e.target.value)}
                  placeholder="Ex: Rugby, Piano..."
                  className="h-8 w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 text-sm transition-colors hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25"
                />
              </div>
            )}

            {/* Ligne 3 : Jour chips + Horaires */}
            <div className="flex items-end gap-3">
              <div className="min-w-0 flex-1">
                <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wider text-neutral-400">Jour</label>
                <div className="flex gap-1">
                  {WORK_DAYS.map((d) => {
                    const colors = DAY_COLORS[d]
                    const isSelected = form.days.includes(d)
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => form.toggleDay(d)}
                        className={cn(
                          'h-8 flex-1 rounded-md text-[10px] font-bold uppercase tracking-wide transition-all',
                          isSelected
                            ? cn(colors.bg, colors.accent, 'border', colors.border, 'shadow-sm')
                            : 'border border-neutral-200 bg-white text-neutral-400 hover:border-neutral-300',
                        )}
                      >
                        {DAY_LABELS[d]}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="flex items-end gap-1.5">
                <div>
                  <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wider text-neutral-400">Debut</label>
                  <div className="relative">
                    <Clock className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" />
                    <select
                      value={form.startTime}
                      onChange={(e) => form.setStartTime(e.target.value)}
                      className="h-8 w-[5.5rem] appearance-none rounded-lg border border-neutral-300 bg-neutral-50 pl-7 pr-1 text-xs font-semibold tabular-nums transition-colors hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25"
                    >
                      {TIME_SLOTS.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <span className="pb-1.5 text-xs text-neutral-300">—</span>
                <div>
                  <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wider text-neutral-400">Fin</label>
                  <div className="relative">
                    <Clock className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" />
                    <select
                      value={form.endTime}
                      onChange={(e) => form.setEndTime(e.target.value)}
                      className="h-8 w-[5.5rem] appearance-none rounded-lg border border-neutral-300 bg-neutral-50 pl-7 pr-1 text-xs font-semibold tabular-nums transition-colors hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25"
                    >
                      {TIME_SLOTS.filter((t) => t > form.startTime).map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {form.canSubmit && (
                  <span className="pb-1.5 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold text-primary">
                    {formatDuration(form.startTime, form.endTime)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Droite : preview live + bouton */}
        <div className="flex w-80 flex-col items-center justify-center bg-neutral-50/50 px-6 py-4">
          <span className="mb-2 text-[9px] font-semibold uppercase tracking-widest text-neutral-400">Aperçu</span>

          {/* Mini card preview */}
          <div className="w-full overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-md">
            <div className={cn('flex items-center gap-1.5 px-3 py-1.5', dayColors.bg)}>
              {form.days.map((d) => (
                <span key={d} className={cn('text-[10px] font-bold uppercase tracking-wider', DAY_COLORS[d]?.accent ?? dayColors.accent)}>
                  {DAY_LABELS[d]}
                </span>
              ))}
            </div>
            <div className="space-y-1 px-3 py-2">
              <span
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
                style={{ backgroundColor: couleur + '18', color: couleur }}
              >
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: couleur }} />
                {form.selectedType?.libelle}
              </span>
              <p className="truncate text-xs font-semibold text-neutral-800">{displayTitle}</p>
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1 text-[10px] text-neutral-500">
                  <Clock className="h-2.5 w-2.5" />
                  {form.startTime} - {form.endTime}
                </span>
                {form.canSubmit && (
                  <span className="text-[9px] font-bold text-primary">
                    {formatDuration(form.startTime, form.endTime)}
                  </span>
                )}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={!form.canSubmit || isPending}
            className={cn(
              'mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-semibold text-white transition-all',
              form.canSubmit && !isPending
                ? 'bg-primary shadow-md shadow-primary/25 hover:bg-primary/90'
                : 'cursor-not-allowed bg-neutral-200 text-neutral-400',
            )}
          >
            {isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Ajouter
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main Component ──────────────────────────────────────

interface ActivitesTabProps {
  etudiantId: number
}

export function ActivitesTab({ etudiantId }: ActivitesTabProps) {
  const { t } = useTranslation()
  const { data: activities, isLoading } = useActivites(etudiantId)
  const { data: typesActivite } = useTypesActivite()
  const createActivite = useCreateActivite(etudiantId)
  const deleteActivite = useDeleteActivite(etudiantId)

  const [expandedDays, setExpandedDays] = useState<Set<WeekDay>>(new Set(WORK_DAYS))

  const toggleDay = (day: WeekDay) => {
    setExpandedDays((prev) => {
      const next = new Set(prev)
      if (next.has(day)) next.delete(day)
      else next.add(day)
      return next
    })
  }

  // Group activities by day, sorted by startTime
  const grouped = useMemo(() => {
    if (!activities) return []
    return WORK_DAYS.map((day) => ({
      day,
      items: activities
        .filter((a) => a.day === day)
        .sort((a, b) => a.startTime.localeCompare(b.startTime)),
    })).filter((g) => g.items.length > 0)
  }, [activities])

  const handleAdd = async (dtos: CreateActivityDto[]) => {
    for (const dto of dtos) {
      await createActivite.mutateAsync(dto)
    }
  }

  const [deleteDialog, setDeleteDialog] = useState<number | null>(null)

  const handleDelete = (activityId: number) => {
    setDeleteDialog(activityId)
  }

  const handleDeleteConfirm = () => {
    if (deleteDialog) {
      deleteActivite.mutate(deleteDialog)
      setDeleteDialog(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <ActivityForm
        etudiantId={etudiantId}
        typesActivite={typesActivite ?? []}
        isPending={createActivite.isPending}
        onSubmit={handleAdd}
      />

      <div className="space-y-3">
        {grouped.map(({ day, items }) => {
          const colors = DAY_COLORS[day]
          const isExpanded = expandedDays.has(day)
          return (
            <DayCard
              key={day}
              day={day}
              items={items}
              colors={colors}
              isExpanded={isExpanded}
              onToggle={() => toggleDay(day)}
              onDelete={handleDelete}
              isDeleting={deleteActivite.isPending}
            />
          )
        })}
        {grouped.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-neutral-200 py-12">
            <p className="text-sm text-neutral-400">
              {t('student.activities.list.noActivities', 'Aucune activité enregistrée')}
            </p>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!deleteDialog}
        variant="danger"
        title={t('student.activities.confirmDelete', 'Êtes-vous sûr de vouloir supprimer cette activité ?')}
        confirmLabel="Supprimer"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog(null)}
      />
    </div>
  )
}

// ── Day Card ────────────────────────────────────────────

interface DayCardProps {
  day: WeekDay
  items: ActivityResponseDto[]
  colors: { bg: string; border: string; accent: string }
  isExpanded: boolean
  onToggle: () => void
  onDelete: (id: number) => void
  isDeleting: boolean
}

function DayCard({ day, items, colors, isExpanded, onToggle, onDelete, isDeleting }: DayCardProps) {
  const { t } = useTranslation()

  return (
    <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={onToggle}
        className={cn('flex w-full items-center justify-between px-5 py-3', colors.bg)}
      >
        <div className="flex items-center gap-3">
          <span className={cn('text-sm font-bold uppercase tracking-wider', colors.accent)}>
            {DAY_LABELS[day]}
          </span>
          <span className="rounded-full bg-white/70 px-2 py-0.5 text-xs font-medium text-neutral-600">
            {items.length} activité{items.length > 1 ? 's' : ''}
          </span>
        </div>
        {isExpanded ? (
          <ChevronDown className={cn('h-4 w-4', colors.accent)} />
        ) : (
          <ChevronRight className={cn('h-4 w-4', colors.accent)} />
        )}
      </button>
      {isExpanded && (
        <div className="divide-y divide-neutral-100">
          {items.map((activity) => (
            <ActivityRow
              key={activity.id}
              activity={activity}
              onDelete={() => onDelete(activity.id)}
              isDeleting={isDeleting}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ── Activity Row ────────────────────────────────────────

interface ActivityRowProps {
  activity: ActivityResponseDto
  onDelete: () => void
  isDeleting: boolean
}

function ActivityRow({ activity, onDelete, isDeleting }: ActivityRowProps) {
  const type = activity.typeActivite
  const couleur = type.couleur ?? '#6B7280'

  return (
    <div className="flex items-center gap-4 px-5 py-3 transition-colors hover:bg-neutral-50/50">
      <span
        className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
        style={{ backgroundColor: couleur + '20', color: couleur }}
      >
        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: couleur }} />
        {type.libelle}
      </span>
      <span className="flex-1 text-sm font-medium text-neutral-800">{activity.title}</span>
      <span className="flex items-center gap-1.5 text-sm text-neutral-500">
        <Clock className="h-3.5 w-3.5" />
        {activity.startTime} - {activity.endTime}
      </span>
      <span className="w-12 text-right text-xs font-medium text-neutral-400">
        {formatDuration(activity.startTime, activity.endTime)}
      </span>
      <button
        type="button"
        onClick={onDelete}
        disabled={isDeleting}
        className="rounded-lg p-1.5 text-neutral-300 transition-colors hover:bg-red-50 hover:text-red-500 disabled:opacity-50"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}
